import React, { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import toast from "react-hot-toast";
import { DsAlert, DsButton, DsSpinner } from "../../../../design-system";
import { salaryStructureService, salaryService } from "../../../../services/api";

function formatInr(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN")}`;
}

/**
 * Hard-delete confirmation for latest salary structure / revision (backend-driven impact).
 */
export default function StructureRevisionDeleteModal({
  isOpen,
  onClose,
  employeeId,
  structureId = null,
  salaryRevisionId = null,
  onDeleted,
}) {
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [impact, setImpact] = useState(null);

  useEffect(() => {
    if (!isOpen || !employeeId) return undefined;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setImpact(null);
      try {
        let res;
        if (structureId) {
          res = await salaryStructureService.getStructureDeleteImpact(employeeId, structureId);
        } else if (salaryRevisionId) {
          res = await salaryService.getSalaryDeleteImpact(salaryRevisionId);
        } else {
          throw new Error("Missing structure or revision id");
        }
        if (!cancelled) setImpact(res?.data || null);
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.response?.data?.message || err.message || "Failed to load delete impact");
          onClose?.();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, employeeId, structureId, salaryRevisionId, onClose]);

  const handleDelete = async () => {
    if (!impact?.canDelete) return;
    setDeleting(true);
    try {
      if (structureId) {
        await salaryStructureService.deleteStructureVersion(employeeId, structureId);
      } else if (salaryRevisionId) {
        await salaryService.deleteSalaryRevision(salaryRevisionId);
      }
      toast.success("Structure revision permanently deleted");
      onDeleted?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10040] flex items-center justify-center bg-black/50 p-4">
      <div
        className="flex w-full max-w-lg flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="structure-delete-title"
      >
        <div className="flex items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-5 py-4">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-[hsl(var(--ds-danger)/0.12)] p-2 text-[hsl(var(--ds-danger))]">
              <AlertTriangle size={20} strokeWidth={1.75} />
            </div>
            <div>
              <h3 id="structure-delete-title" className="ds-h4 text-[hsl(var(--ds-fg))]">
                Delete structure revision?
              </h3>
              <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
                This action is permanent and cannot be undone.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 opacity-70 hover:opacity-100"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <DsSpinner className="h-8 w-8" />
            </div>
          ) : impact ? (
            <>
              {!impact.canDelete ? (
                <DsAlert tone="danger">{impact.blockReason || impact.summary}</DsAlert>
              ) : (
                <>
                  <p className="ds-body text-[hsl(var(--ds-fg))]">{impact.summary}</p>
                  <div className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-3 text-sm">
                    <div className="flex justify-between gap-2">
                      <span className="text-[hsl(var(--ds-muted-fg))]">Effective from</span>
                      <span className="font-medium">{impact.effectiveFrom || "—"}</span>
                    </div>
                    <div className="mt-2 flex justify-between gap-2">
                      <span className="text-[hsl(var(--ds-muted-fg))]">CTC being removed</span>
                      <span className="font-medium">{formatInr(impact.deletedCtc)}</span>
                    </div>
                    {impact.restoredCtc != null ? (
                      <div className="mt-2 flex justify-between gap-2">
                        <span className="text-[hsl(var(--ds-muted-fg))]">Profile CTC after restore</span>
                        <span className="font-medium text-[hsl(var(--ds-success))]">
                          {formatInr(impact.restoredCtc)}
                        </span>
                      </div>
                    ) : null}
                  </div>
                  <DsAlert tone="warning">
                    <ul className="list-disc space-y-1 pl-4 text-left">
                      {(impact.warnings || []).map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </DsAlert>
                </>
              )}
            </>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-3">
          <DsButton type="button" variant="outline" size="sm" onClick={onClose} disabled={deleting}>
            Cancel
          </DsButton>
          <DsButton
            type="button"
            variant="danger"
            size="sm"
            onClick={handleDelete}
            disabled={loading || deleting || !impact?.canDelete}
          >
            {deleting ? "Deleting…" : "Delete permanently"}
          </DsButton>
        </div>
      </div>
    </div>
  );
}
